/**
 * DataChart: one Carbon Charts visualisation for a business view (Carbon data visualization: a line for trends over time, a
 * horizontal bar for rankings, a donut for a part of a whole with few categories, a heatmap for two dimensions such as weekday
 * by hour), always with the same figures as a real table behind a "Chart / Table" switch (a chart is not accessible on its own).
 * The chart library loads on first use (`chartLib.ts`), in the `white` theme like `TrendChart`.
 *
 * Props: `kind` ('line' | 'bar' | 'donut' | 'heatmap'), `data` (`{ group, key, value }`: the series, or for a donut the slice,
 * is `group`; the x position, bar or column is `key`; for a heatmap `group` is the row), `table` (`headers` and pre-formatted
 * `rows`, the first column a label, the others numbers), `summary` (the chart's accessible name), `formatValue`, `centerLabel`
 * (a donut's middle) and `legendTitle` (a heatmap's colour scale).
 */
import {
  ContentSwitcher,
  SkeletonText,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { lazy, Suspense, useMemo, useState } from 'react';
import './overview.scss';

const charts = {
  line: lazy(() => import('./chartLib').then((m) => ({ default: m.LineChart }))),
  bar: lazy(() => import('./chartLib').then((m) => ({ default: m.SimpleBarChart }))),
  donut: lazy(() => import('./chartLib').then((m) => ({ default: m.DonutChart }))),
  heatmap: lazy(() => import('./chartLib').then((m) => ({ default: m.HeatmapChart }))),
};

export interface ChartDatum {
  group: string;
  key: string;
  value: number;
}

export interface DataChartProps {
  kind: 'line' | 'bar' | 'donut' | 'heatmap';
  data: readonly ChartDatum[];
  table: { headers: readonly string[]; rows: readonly (readonly string[])[] };
  summary: string;
  formatValue(value: number): string;
  centerLabel?: string;
  legendTitle?: string;
}

export function DataChart({ kind, data, table, summary, formatValue, centerLabel, legendTitle }: DataChartProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const series = useMemo(() => new Set(data.map((d) => d.group)).size, [data]);
  const options = useMemo(() => {
    // No Carbon token for a chart's height: a fixed 16rem plot (20rem for a long ranking), as TrendChart notes.
    const base = {
      theme: 'white',
      height: kind === 'bar' && data.length > 6 ? '20rem' : '16rem',
      resizable: true,
      toolbar: { enabled: false },
      tooltip: { valueFormatter: (v: number) => formatValue(v) },
    };
    switch (kind) {
      case 'line':
        return {
          ...base,
          legend: { enabled: series > 1, alignment: 'center' },
          points: { enabled: true, radius: 3 },
          axes: {
            left: { mapsTo: 'value', ticks: { formatter: (v: number) => formatValue(v) } },
            bottom: { mapsTo: 'key', scaleType: 'labels' },
          },
        };
      case 'bar':
        // Horizontal: the categories down the side keep long names readable; the biggest first.
        return {
          ...base,
          legend: { enabled: false },
          axes: {
            left: { mapsTo: 'key', scaleType: 'labels' },
            bottom: { mapsTo: 'value', ticks: { formatter: (v: number) => formatValue(v) } },
          },
        };
      case 'donut':
        return {
          ...base,
          legend: { alignment: 'center' },
          pie: { valueMapsTo: 'value', labels: { enabled: false } },
          donut: {
            alignment: 'center',
            center: { label: centerLabel ?? '', numberFormatter: (v: number) => formatValue(v) },
          },
        };
      case 'heatmap':
        return {
          ...base,
          axes: {
            bottom: { mapsTo: 'key', scaleType: 'labels' },
            left: { mapsTo: 'group', scaleType: 'labels' },
          },
          heatmap: { colorLegend: { title: legendTitle ?? '' } },
        };
    }
  }, [centerLabel, data.length, formatValue, kind, legendTitle, series]);
  const Chart = charts[kind];
  // A donut takes its slices as groups; the others as they come.
  const chartData = useMemo(
    () => (kind === 'donut' ? data.map((d) => ({ group: d.key, value: d.value })) : data),
    [data, kind],
  );

  return (
    <div className="pos-trend-chart">
      <ContentSwitcher
        selectedIndex={view === 'chart' ? 0 : 1}
        onChange={({ name }) => setView(name === 'table' ? 'table' : 'chart')}
        aria-label={t('admin.overview.chart.view')}
        size="sm"
      >
        <Switch name="chart" text={t('admin.overview.chart.chart')} />
        <Switch name="table" text={t('admin.overview.chart.table')} />
      </ContentSwitcher>
      {view === 'chart' ? (
        <div role="img" aria-label={summary} className="pos-trend-chart__plot">
          <Suspense fallback={<SkeletonText paragraph lineCount={4} />}>
            <Chart data={chartData as never} options={options as never} />
          </Suspense>
        </div>
      ) : (
        <Table aria-label={summary} size="sm">
          <TableHead>
            <TableRow>
              {table.headers.map((h, i) => (
                <TableHeader key={h} className={i > 0 ? 'pos-trend-chart__number' : undefined}>
                  {h}
                </TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row) => (
              <TableRow key={row.join('|')}>
                {row.map((cell, i) => (
                  <TableCell
                    key={`${table.headers[i]}-${cell}`}
                    className={i > 0 ? 'pos-trend-chart__number' : undefined}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
